import {
  useEffect,
  useState
} from "react";

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from "../access/firestore";

import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Save,
  AlertTriangle
} from "lucide-react";

import { db } from "../firebase";
import { useClinic } from "../context/ClinicContext";
import { requestVexaConfirm } from "../notifications/dialogService";


const HISTORIA_CLINICA_DEFAULT = {
  version: 2,

  secciones: [
    {
      id: "general",
      titulo: "General",
      preguntas: [
        {
          id: "nombre",
          texto: "Nombre",
          tipo: "texto",
          campo: "nombre",
          obligatorio: true
        },
        {
          id: "apellidos",
          texto: "Apellidos",
          tipo: "texto",
          campo: "apellidos",
          obligatorio: true
        },
        {
          id: "sexo",
          texto: "Sexo",
          tipo: "seleccion",
          campo: "sexo",
          opciones: [
            "Masculino",
            "Femenino",
            "Otro"
          ]
        },
        {
          id: "estado_civil",
          texto: "Estado civil",
          tipo: "seleccion",
          campo: "estadoCivil",
          opciones: [
            "Soltero(a)",
            "Casado(a)",
            "Unión libre",
            "Divorciado(a)",
            "Viudo(a)"
          ]
        },
        {
          id: "fecha_nacimiento",
          texto: "Nacimiento",
          tipo: "fecha",
          campo: "fechaNacimiento"
        },
        {
          id: "edad",
          texto: "Edad",
          tipo: "numero",
          campo: "edad",
          soloLectura: true
        },
        {
          id: "peso",
          texto: "Peso (kg)",
          tipo: "numero",
          campo: "peso"
        },
        {
          id: "telefono",
          texto: "Teléfono de Casa",
          tipo: "texto",
          campo: "telefono"
        },
        {
          id: "whatsapp",
          texto: "WhatsApp",
          tipo: "texto",
          campo: "whatsapp"
        },
        {
          id: "correo",
          texto: "Correo",
          tipo: "texto",
          campo: "correo"
        },
        {
          id: "curp",
          texto: "CURP",
          tipo: "texto",
          campo: "curp"
        },
        {
          id: "rfc",
          texto: "RFC",
          tipo: "texto",
          campo: "rfc"
        },
        {
          id: "ocupacion",
          texto: "Ocupación",
          tipo: "texto",
          campo: "ocupacion"
        },
        {
          id: "nacionalidad",
          texto: "Nacionalidad",
          tipo: "texto",
          campo: "nacionalidad"
        }
      ]
    },

    {
      id: "direccion",
      titulo: "Dirección",
      preguntas: [
        {
          id: "calle",
          texto: "Calle",
          tipo: "texto",
          campo: "calle"
        },
        {
          id: "numero_exterior",
          texto: "No. exterior",
          tipo: "texto",
          campo: "numeroExterior"
        },
        {
          id: "numero_interior",
          texto: "No. interior",
          tipo: "texto",
          campo: "numeroInterior"
        },
        {
          id: "cp",
          texto: "Código postal",
          tipo: "texto",
          campo: "cp"
        },
        {
          id: "colonia",
          texto: "Colonia",
          tipo: "texto",
          campo: "colonia"
        },
        {
          id: "ciudad",
          texto: "Ciudad",
          tipo: "texto",
          campo: "ciudad"
        },
        {
          id: "estado",
          texto: "Estado",
          tipo: "texto",
          campo: "estado",
          soloLectura: true
        },
        {
          id: "pais",
          texto: "País",
          tipo: "texto",
          campo: "pais",
          soloLectura: true
        }
      ]
    },

    {
      id: "clinico",
      titulo: "Clínico",
      preguntas: [
        {
          id: "motivo_consulta",
          texto: "Motivo de consulta",
          tipo: "texto_largo",
          campo: "motivoConsulta",
          obligatorio: true
        },
        {
          id: "padecimiento_actual",
          texto: "Padecimiento actual",
          tipo: "texto_largo",
          campo: "padecimientoActual"
        },
        {
          id: "dolor",
          texto: "Descripción del dolor",
          tipo: "texto",
          campo: "dolor"
        },
        {
          id: "escala_dolor",
          texto: "Escala del dolor",
          tipo: "numero",
          campo: "escalaDolor"
        },
        {
          id: "estado_salud_general",
          texto: "Salud general",
          tipo: "seleccion",
          campo: "estadoSaludGeneral",
          opciones: [
            "Buena",
            "Regular",
            "Mala"
          ]
        },
        {
          id: "covid_reciente",
          texto: "¿COVID reciente?",
          tipo: "si_no",
          campo: "covidReciente"
        },
        {
          id: "alergias",
          texto: "Alergias",
          tipo: "texto_largo",
          campo: "alergias",
          generaAlerta: true,
          alertaTexto: "Alergias"
        },
        {
          id: "antecedentes_medicos",
          texto: "Antecedentes médicos",
          tipo: "texto_largo",
          campo: "antecedentesMedicos"
        },
        {
          id: "medicamentos",
          texto: "Medicamentos actuales",
          tipo: "texto_largo",
          campo: "medicamentos"
        },
        {
          id: "cirugias",
          texto: "¿Ha tenido cirugías?",
          tipo: "si_no",
          campo: "cirugias",
          permitirEspecificar: true,
          especificacionLabel: "Especifique las cirugías"
        },
        {
          id: "transfusiones",
          texto: "¿Ha recibido transfusiones sanguíneas?",
          tipo: "si_no",
          campo: "transfusiones",
          permitirEspecificar: true,
          especificacionLabel: "Especifique"
        },
        {
          id: "diabetes",
          texto: "¿Padece diabetes?",
          tipo: "si_no",
          campo: "diabetes",
          generaAlerta: true,
          alertaTexto: "Paciente diabético"
        },
        {
          id: "hipertension",
          texto: "¿Padece hipertensión arterial?",
          tipo: "si_no",
          campo: "hipertension",
          generaAlerta: true,
          alertaTexto: "Hipertensión arterial"
        },
        {
          id: "cardiopatias",
          texto: "¿Padece alguna cardiopatía?",
          tipo: "si_no",
          campo: "cardiopatias",
          generaAlerta: true,
          alertaTexto: "Cardiopatías"
        },
        {
          id: "asma",
          texto: "¿Padece asma?",
          tipo: "si_no",
          campo: "asma",
          generaAlerta: true,
          alertaTexto: "Asma"
        },
        {
          id: "embarazo",
          texto: "¿Está embarazada?",
          tipo: "si_no",
          campo: "embarazo",
          generaAlerta: true,
          alertaTexto: "Embarazo"
        },
        {
          id: "epilepsia",
          texto: "¿Padece epilepsia?",
          tipo: "si_no",
          campo: "epilepsia",
          generaAlerta: true,
          alertaTexto: "Epilepsia"
        },
        {
          id: "hepatitis",
          texto: "¿Padece o ha padecido hepatitis?",
          tipo: "si_no",
          campo: "hepatitis",
          generaAlerta: true,
          alertaTexto: "Hepatitis"
        },
        {
          id: "cancer",
          texto: "¿Tiene o ha tenido cáncer?",
          tipo: "si_no",
          campo: "cancer",
          permitirEspecificar: true,
          especificacionLabel: "Especifique",
          generaAlerta: true,
          alertaTexto: "Antecedente de cáncer"
        },
        {
          id: "ets",
          texto: "¿Tiene o ha tenido una ETS?",
          tipo: "si_no",
          campo: "ets",
          permitirEspecificar: true,
          especificacionLabel: "Especifique",
          generaAlerta: true,
          alertaTexto: "Antecedente de ETS"
        },
        {
          id: "drogas",
          texto: "¿Consume drogas?",
          tipo: "si_no",
          campo: "drogas",
          permitirEspecificar: true,
          especificacionLabel: "Sustancia / frecuencia",
          generaAlerta: true,
          alertaTexto: "Consumo de drogas"
        },
        {
          id: "anticonceptivos",
          texto: "¿Utiliza anticonceptivos?",
          tipo: "si_no",
          campo: "anticonceptivos"
        },
        {
          id: "osteoporosis",
          texto: "¿Padece osteoporosis?",
          tipo: "si_no",
          campo: "osteoporosis",
          generaAlerta: true,
          alertaTexto: "Osteoporosis"
        },
        {
          id: "anticoagulantes",
          texto: "¿Utiliza anticoagulantes?",
          tipo: "si_no",
          campo: "anticoagulantes",
          permitirEspecificar: true,
          especificacionLabel: "Especifique cuál",
          generaAlerta: true,
          alertaTexto: "Uso de anticoagulantes"
        },
        {
          id: "cancer_detalle",
          texto: "Especifique cáncer",
          tipo: "texto",
          campo: "cancerDetalle"
        },
        {
          id: "ets_detalle",
          texto: "Especifique ETS",
          tipo: "texto",
          campo: "etsDetalle"
        },
        {
          id: "drogas_sustancia",
          texto: "Sustancia / frecuencia de consumo",
          tipo: "texto",
          campo: "drogasSustancia"
        },
        {
          id: "otros_padecimientos",
          texto: "Otros padecimientos",
          tipo: "texto_largo",
          campo: "otrosPadecimientos",
          generaAlerta: true,
          alertaTexto: "Otros padecimientos"
        },
        {
          id: "tabaco",
          texto: "¿Fuma?",
          tipo: "si_no",
          campo: "tabaco",
          permitirEspecificar: true,
          especificacionLabel: "Cantidad / frecuencia",
          generaAlerta: true,
          alertaTexto: "Paciente fumador"
        },
        {
          id: "alcohol",
          texto: "¿Consume alcohol?",
          tipo: "si_no",
          campo: "alcohol",
          permitirEspecificar: true,
          especificacionLabel: "Cantidad / frecuencia"
        },
        {
          id: "bruxismo",
          texto: "¿Presenta bruxismo?",
          tipo: "si_no",
          campo: "bruxismo",
          permitirEspecificar: true,
          especificacionLabel: "Especifique",
          generaAlerta: true,
          alertaTexto: "Bruxismo"
        },
        {
          id: "cepillados_dia",
          texto: "¿Cuántas veces al día se cepilla los dientes?",
          tipo: "numero",
          campo: "cepilladosDia"
        },
        {
          id: "hilo_dental",
          texto: "¿Utiliza hilo dental?",
          tipo: "si_no",
          campo: "hiloDental"
        },
        {
          id: "enjuague",
          texto: "¿Utiliza enjuague bucal?",
          tipo: "si_no",
          campo: "enjuague"
        },
        {
          id: "sangrado_encias",
          texto: "¿Presenta sangrado de encías?",
          tipo: "si_no",
          campo: "sangradoEncias"
        },
        {
          id: "gingivitis",
          texto: "¿Presenta gingivitis?",
          tipo: "si_no",
          campo: "gingivitis"
        },
        {
          id: "enfermedad_periodontal",
          texto: "¿Presenta enfermedad periodontal?",
          tipo: "si_no",
          campo: "enfermedadPeriodontal",
          generaAlerta: true,
          alertaTexto: "Enfermedad periodontal"
        },
        {
          id: "halitosis",
          texto: "¿Presenta halitosis?",
          tipo: "si_no",
          campo: "halitosis"
        },
        {
          id: "movilidad_dental",
          texto: "¿Presenta movilidad dental?",
          tipo: "si_no",
          campo: "movilidadDental"
        },
        {
          id: "dificultad_masticar",
          texto: "¿Presenta dificultad para masticar?",
          tipo: "si_no",
          campo: "dificultadMasticar"
        }
      ]
    },

    {
      id: "extras",
      titulo: "Extras",
      preguntas: [
        {
          id: "seguro_medico",
          texto: "Seguro médico",
          tipo: "texto",
          campo: "seguroMedico"
        },
        {
          id: "numero_poliza",
          texto: "Número de póliza",
          tipo: "texto",
          campo: "numeroPoliza"
        },
        {
          id: "contacto_emergencia",
          texto: "Contacto de emergencia",
          tipo: "texto",
          campo: "contactoEmergencia"
        },
        {
          id: "telefono_emergencia",
          texto: "Teléfono de emergencia",
          tipo: "texto",
          campo: "telefonoEmergencia"
        },
        {
          id: "parentesco_emergencia",
          texto: "Parentesco",
          tipo: "texto",
          campo: "parentescoEmergencia"
        },
        {
          id: "observaciones",
          texto: "Observaciones",
          tipo: "texto_largo",
          campo: "observaciones"
        }
      ]
    }
  ]
};


function normalizarConfiguracionHistoria(data) {
  const base = JSON.parse(
    JSON.stringify(HISTORIA_CLINICA_DEFAULT)
  );

  if (!data || !Array.isArray(data.secciones)) {
    return base;
  }

  const idsFijos = [
    "general",
    "direccion",
    "clinico",
    "extras"
  ];

  if (
    Number(data.version) >= 2 &&
    idsFijos.every(id =>
      data.secciones.some(
        s => s.id === id
      )
    )
  ) {
    return {
      version: Number(data.version),
      secciones: idsFijos.map(id => {
        const seccion =
          data.secciones.find(
            s => s.id === id
          );

        return {
          id,
          titulo:
            id === "general"
              ? "General"
              : id === "direccion"
                ? "Dirección"
                : id === "clinico"
                  ? "Clínico"
                  : "Extras",
          preguntas:
            Array.isArray(
              seccion.preguntas
            )
              ? seccion.preguntas
              : []
        };
      })
    };
  }

  const porId = new Map();

  base.secciones.forEach(
    seccion => {
      seccion.preguntas.forEach(
        pregunta => {
          porId.set(
            pregunta.id,
            {
              seccion,
              pregunta
            }
          );
        }
      );
    }
  );

  const mapaSeccion = {
    datos_generales: "general",
    general: "general",
    direccion: "direccion",
    clinico: "clinico",
    habitos: "clinico",
    higiene_oral: "clinico",
    dolor: "clinico",
    motivo_consulta: "extras",
    observaciones: "extras",
    extras: "extras"
  };

  data.secciones.forEach(
    seccionOriginal => {

      const destinoId =
        mapaSeccion[
          seccionOriginal.id
        ] ||
        (
          base.secciones.some(
            s =>
              s.id ===
              seccionOriginal.id
          )
            ? seccionOriginal.id
            : "extras"
        );

      const destino =
        base.secciones.find(
          s =>
            s.id ===
            destinoId
        );

      if (
        !destino ||
        !Array.isArray(
          seccionOriginal.preguntas
        )
      ) {
        return;
      }

      seccionOriginal.preguntas.forEach(
        preguntaOriginal => {

          const existente =
            porId.get(
              preguntaOriginal.id
            );

          if (existente) {
            existente.seccion.preguntas =
              existente.seccion.preguntas.map(
                p =>
                  p.id ===
                  preguntaOriginal.id
                    ? {
                        ...p,
                        ...preguntaOriginal
                      }
                    : p
              );

            return;
          }

          destino.preguntas.push({
            obligatorio: false,
            permitirEspecificar: false,
            especificacionLabel:
              "Especifique",
            opciones: [],
            generaAlerta: false,
            alertaTexto: "",
            ...preguntaOriginal
          });
        }
      );
    }
  );

  return {
    version: 2,
    secciones: base.secciones
  };
}


export default function MobileHistoriaClinicaSettings() {

  const { clinicId } = useClinic();

  const [
    configuracion,
    setConfiguracion
  ] = useState(
    HISTORIA_CLINICA_DEFAULT
  );

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    seccionAbierta,
    setSeccionAbierta
  ] = useState(null);


  useEffect(() => {

    if (!clinicId) {
      setLoading(false);
      return;
    }

    const cargar = async () => {

      try {

        setLoading(true);

        const ref = doc(
          db,
          "clinics",
          clinicId,
          "historiaClinicaConfig",
          "config"
        );

        const snapshot =
          await getDoc(ref);

        if (
          snapshot.exists()
        ) {

          const data =
            snapshot.data();

          if (
            data &&
            Array.isArray(
              data.secciones
            )
          ) {

            setConfiguracion(
              normalizarConfiguracionHistoria(
                data
              )
            );

            return;
          }
        }

        setConfiguracion(
          JSON.parse(
            JSON.stringify(
              HISTORIA_CLINICA_DEFAULT
            )
          )
        );

      } catch (error) {

        console.error(
          "Error cargando historia clínica:",
          error
        );

        setConfiguracion(
          JSON.parse(
            JSON.stringify(
              HISTORIA_CLINICA_DEFAULT
            )
          )
        );

      } finally {

        setLoading(false);

      }
    };

    cargar();

  }, [clinicId]);


  const actualizarPregunta = (
    seccionId,
    preguntaId,
    campo,
    valor
  ) => {

    setConfiguracion(prev => ({
      ...prev,

      secciones:
        prev.secciones.map(
          seccion =>
            seccion.id ===
            seccionId
              ? {
                  ...seccion,

                  preguntas:
                    seccion.preguntas.map(
                      pregunta =>
                        pregunta.id ===
                        preguntaId
                          ? {
                              ...pregunta,
                              [campo]:
                                valor
                            }
                          : pregunta
                    )
                }
              : seccion
        )
    }));

  };


  const agregarPregunta = (
    seccionId
  ) => {

    const nuevaPregunta = {
      id:
        `pregunta_${Date.now()}`,

      texto:
        "Nueva pregunta",

      tipo:
        "texto",

      obligatorio:
        false,

      permitirEspecificar:
        false,

      especificacionLabel:
        "Especifique",

      opciones: [],

      generaAlerta:
        false,

      alertaTexto:
        ""
    };

    setConfiguracion(prev => ({
      ...prev,

      secciones:
        prev.secciones.map(
          seccion =>
            seccion.id ===
            seccionId
              ? {
                  ...seccion,

                  preguntas: [
                    ...seccion.preguntas,
                    nuevaPregunta
                  ]
                }
              : seccion
        )
    }));

  };


  const eliminarPregunta = async (
    seccionId,
    preguntaId
  ) => {

    if (
      !(await requestVexaConfirm(
        "¿Eliminar esta pregunta?"
      ))
    ) {
      return;
    }

    setConfiguracion(prev => ({
      ...prev,

      secciones:
        prev.secciones.map(
          seccion =>
            seccion.id ===
            seccionId
              ? {
                  ...seccion,

                  preguntas:
                    seccion.preguntas.filter(
                      pregunta =>
                        pregunta.id !==
                        preguntaId
                    )
                }
              : seccion
        )
    }));

  };


  const guardar = async () => {

    if (!clinicId) {
      alert(
        "No se encontró el consultorio."
      );
      return;
    }

    try {

      setSaving(true);

      const configuracionFinal =
        normalizarConfiguracionHistoria({
          ...configuracion,
          version: 2
        });

      await setDoc(
        doc(
          db,
          "clinics",
          clinicId,
          "historiaClinicaConfig",
          "config"
        ),
        {
          ...configuracionFinal,
          updatedAt:
            serverTimestamp()
        },
        {
          merge: true
        }
      );

      setConfiguracion(
        configuracionFinal
      );

      alert(
        "Historia clínica guardada correctamente."
      );

    } catch (error) {

      console.error(
        "Error guardando historia clínica:",
        error
      );

      alert(
        "No fue posible guardar la historia clínica."
      );

    } finally {

      setSaving(false);

    }
  };


  if (loading) {

    return (
      <div
        style={{
          padding: 30,
          textAlign: "center",
          color: "#64748b"
        }}
      >
        Cargando historia clínica...
      </div>
    );

  }


  return (
    <div
      style={{
        padding: "4px 0 24px"
      }}
    >

      <div
        style={{
          marginBottom: 18
        }}
      >

        <h2
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 800,
            color: "#111827"
          }}
        >
          Historia clínica
        </h2>

        <p
          style={{
            margin:
              "6px 0 0",
            fontSize: 13,
            lineHeight: 1.5,
            color: "#64748b"
          }}
        >
          Personaliza las preguntas
          que forman parte del
          expediente clínico.
        </p>

      </div>


      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: 14,
          marginBottom: 18,
          borderRadius: 14,
          background: "#eff6ff",
          border:
            "1px solid #bfdbfe",
          color: "#1e40af",
          fontSize: 13,
          lineHeight: 1.5
        }}
      >

        <AlertTriangle
          size={18}
          style={{
            flexShrink: 0,
            marginTop: 1
          }}
        />

        <div>
          Los cambios realizados aquí
          definirán las preguntas
          disponibles en la historia
          clínica del consultorio.
        </div>

      </div>


      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12
        }}
      >

        {configuracion.secciones.map(
          seccion => {

            const abierta =
              seccionAbierta ===
              seccion.id;

            return (
              <div
                key={seccion.id}
                style={{
                  background: "#fff",
                  border:
                    "1px solid #e5e7eb",
                  borderRadius: 18,
                  overflow: "hidden",
                  boxShadow:
                    "0 3px 12px rgba(15,23,42,.05)"
                }}
              >

                <button
                  type="button"
                  onClick={() =>
                    setSeccionAbierta(
                      abierta
                        ? null
                        : seccion.id
                    )
                  }
                  style={{
                    width: "100%",
                    border: "none",
                    background:
                      "#f8fafc",
                    padding:
                      "16px 15px",
                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "space-between",
                    cursor: "pointer",
                    textAlign: "left"
                  }}
                >

                  <div>

                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 800,
                        color:
                          "#111827"
                      }}
                    >
                      {seccion.titulo}
                    </div>

                    <div
                      style={{
                        marginTop: 3,
                        fontSize: 12,
                        color:
                          "#64748b"
                      }}
                    >
                      {seccion.preguntas.length}
                      {" "}
                      preguntas
                    </div>

                  </div>

                  {abierta ? (
                    <ChevronUp
                      size={21}
                      color="#64748b"
                    />
                  ) : (
                    <ChevronDown
                      size={21}
                      color="#64748b"
                    />
                  )}

                </button>


                {abierta && (

                  <div
                    style={{
                      padding: 12,
                      display: "flex",
                      flexDirection:
                        "column",
                      gap: 12
                    }}
                  >

                    {seccion.preguntas.map(
                      pregunta => (

                        <PreguntaMobile
                          key={
                            pregunta.id
                          }
                          pregunta={
                            pregunta
                          }
                          onChange={(
                            campo,
                            valor
                          ) =>
                            actualizarPregunta(
                              seccion.id,
                              pregunta.id,
                              campo,
                              valor
                            )
                          }
                          onDelete={() =>
                            eliminarPregunta(
                              seccion.id,
                              pregunta.id
                            )
                          }
                        />

                      )
                    )}


                    <button
                      type="button"
                      onClick={() =>
                        agregarPregunta(
                          seccion.id
                        )
                      }
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        gap: 8,
                        padding: 13,
                        borderRadius: 12,
                        border:
                          "1px dashed #93c5fd",
                        background:
                          "#eff6ff",
                        color:
                          "#1d4ed8",
                        fontWeight: 700,
                        fontSize: 13
                      }}
                    >

                      <Plus size={17} />

                      Agregar pregunta

                    </button>

                  </div>

                )}

              </div>
            );
          }
        )}

      </div>


      <button
        type="button"
        onClick={guardar}
        disabled={saving}
        style={{
          width: "100%",
          marginTop: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: 14,
          border: "none",
          borderRadius: 13,
          background:
            saving
              ? "#93c5fd"
              : "#2563eb",
          color: "#fff",
          fontWeight: 800,
          fontSize: 14
        }}
      >

        <Save size={18} />

        {saving
          ? "Guardando..."
          : "Guardar historia clínica"}

      </button>

    </div>
  );
}


function PreguntaMobile({
  pregunta,
  onChange,
  onDelete
}) {

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 12px",
    border:
      "1px solid #d1d5db",
    borderRadius: 11,
    background: "#fff",
    fontSize: 13,
    color: "#111827"
  };


  return (
    <div
      style={{
        border:
          "1px solid #e5e7eb",
        borderRadius: 14,
        padding: 13,
        background: "#fff"
      }}
    >

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent:
            "space-between",
          gap: 8,
          marginBottom: 12
        }}
      >

        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: "#64748b",
            textTransform:
              "uppercase"
          }}
        >
          Pregunta
        </span>

        <button
          type="button"
          onClick={onDelete}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            border: "none",
            background:
              "#fee2e2",
            color: "#b91c1c",
            borderRadius: 9,
            padding:
              "7px 9px",
            fontSize: 12,
            fontWeight: 700
          }}
        >
          <Trash2 size={14} />
          Eliminar
        </button>

      </div>


      <label
        style={{
          display: "block",
          marginBottom: 6,
          fontSize: 12,
          fontWeight: 700,
          color: "#374151"
        }}
      >
        Texto de la pregunta
      </label>

      <input
        value={
          pregunta.texto || ""
        }
        onChange={e =>
          onChange(
            "texto",
            e.target.value
          )
        }
        style={{
          ...inputStyle,
          marginBottom: 12
        }}
      />


      <label
        style={{
          display: "block",
          marginBottom: 6,
          fontSize: 12,
          fontWeight: 700,
          color: "#374151"
        }}
      >
        Tipo de respuesta
      </label>

      <select
        value={
          pregunta.tipo ||
          "texto"
        }
        onChange={e =>
          onChange(
            "tipo",
            e.target.value
          )
        }
        style={{
          ...inputStyle,
          marginBottom: 12
        }}
      >

        <option value="texto">
          Campo abierto
        </option>

        <option value="texto_largo">
          Campo abierto largo
        </option>

        <option value="si_no">
          Sí / No
        </option>

        <option value="checkbox">
          Checkbox
        </option>

        <option value="seleccion">
          Selección única
        </option>

        <option value="seleccion_multiple">
          Selección múltiple
        </option>

        <option value="numero">
          Número
        </option>

        <option value="fecha">
          Fecha
        </option>

      </select>


      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          fontSize: 13,
          color: "#374151"
        }}
      >

        <input
          type="checkbox"
          checked={
            !!pregunta.obligatorio
          }
          onChange={e =>
            onChange(
              "obligatorio",
              e.target.checked
            )
          }
        />

        Respuesta obligatoria

      </label>


      {(
        pregunta.tipo ===
          "checkbox" ||
        pregunta.tipo ===
          "si_no"
      ) && (

        <div
          style={{
            padding: 11,
            marginBottom: 12,
            borderRadius: 12,
            background:
              "#f8fafc",
            border:
              "1px solid #e5e7eb"
          }}
        >

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "#374151"
            }}
          >

            <input
              type="checkbox"
              checked={
                !!pregunta.permitirEspecificar
              }
              onChange={e =>
                onChange(
                  "permitirEspecificar",
                  e.target.checked
                )
              }
            />

            Permitir especificar

          </label>


          {pregunta.permitirEspecificar && (

            <input
              value={
                pregunta.especificacionLabel ||
                "Especifique"
              }
              onChange={e =>
                onChange(
                  "especificacionLabel",
                  e.target.value
                )
              }
              placeholder="Texto del campo de especificación"
              style={{
                ...inputStyle,
                marginTop: 10
              }}
            />

          )}

        </div>

      )}


      {(
        pregunta.tipo ===
          "seleccion" ||
        pregunta.tipo ===
          "seleccion_multiple"
      ) && (

        <div
          style={{
            padding: 11,
            marginBottom: 12,
            borderRadius: 12,
            background:
              "#f8fafc",
            border:
              "1px solid #e5e7eb"
          }}
        >

          <label
            style={{
              display: "block",
              marginBottom: 6,
              fontSize: 12,
              fontWeight: 700,
              color: "#374151"
            }}
          >
            Opciones
          </label>

          <textarea
            value={
              (
                pregunta.opciones ||
                []
              ).join("\n")
            }
            onChange={e => {

              const opciones =
                e.target.value
                  .split("\n")
                  .map(
                    item =>
                      item.trim()
                  )
                  .filter(Boolean);

              onChange(
                "opciones",
                opciones
              );

            }}
            placeholder={
              "Una opción por línea"
            }
            style={{
              ...inputStyle,
              minHeight: 90,
              resize: "vertical"
            }}
          />

        </div>

      )}


      <div
        style={{
          padding: 11,
          borderRadius: 12,
          background:
            "#fff7ed",
          border:
            "1px solid #fed7aa"
        }}
      >

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "#374151"
          }}
        >

          <input
            type="checkbox"
            checked={
              !!pregunta.generaAlerta
            }
            onChange={e =>
              onChange(
                "generaAlerta",
                e.target.checked
              )
            }
          />

          Generar alerta clínica

        </label>


        {pregunta.generaAlerta && (

          <input
            value={
              pregunta.alertaTexto ||
              ""
            }
            onChange={e =>
              onChange(
                "alertaTexto",
                e.target.value
              )
            }
            placeholder={
              "Texto que aparecerá como alerta"
            }
            style={{
              ...inputStyle,
              marginTop: 10
            }}
          />

        )}

      </div>

    </div>
  );
}
